import { useState, useEffect, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type { Leaf } from '../types'

interface IntroPageProps {
  onEnter: () => void
}

const LEAF_COLORS = ['#c41e3a', '#1a1a1a', '#c8a951', '#5C6B5C', '#8B4513']
const MAX_LEAVES = 60
const MAX_FPS = 60
const FRAME_MIN_TIME = 1000 / MAX_FPS

// Check for reduced motion preference
const getReducedMotion = () => {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export default function IntroPage({ onEnter }: IntroPageProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [showEnterPrompt, setShowEnterPrompt] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)
  const leavesRef = useRef<Leaf[]>([])
  const animationRef = useRef<number>()
  const lastFrameTimeRef = useRef(0)
  const lastMouseMoveRef = useRef(0)
  const mousePosRef = useRef({ x: 0, y: 0 })

  // Check for reduced motion on mount
  useEffect(() => {
    setReducedMotion(getReducedMotion())
    
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    const handleChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches)
    mediaQuery.addEventListener('change', handleChange)
    
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  // Initialize canvas and start animation
  useEffect(() => {
    if (reducedMotion) return

    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1
      canvas.width = window.innerWidth * dpr
      canvas.height = window.innerHeight * dpr
      canvas.style.width = `${window.innerWidth}px`
      canvas.style.height = `${window.innerHeight}px`
      ctx.scale(dpr, dpr)
    }
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    // Create initial leaves
    for (let i = 0; i < 15; i++) {
      createLeaf(window.innerWidth, window.innerHeight, true)
    }

    // Animation loop with frame limiting
    const animate = (timestamp: number) => {
      if (!ctx || !canvas) return
      
      // Frame limiting for performance
      if (timestamp - lastFrameTimeRef.current < FRAME_MIN_TIME) {
        animationRef.current = requestAnimationFrame(animate)
        return
      }
      lastFrameTimeRef.current = timestamp
      
      const width = window.innerWidth
      const height = window.innerHeight
      
      ctx.clearRect(0, 0, width, height)
      
      leavesRef.current = leavesRef.current.filter(leaf => {
        // Physics calculations
        const time = timestamp * 0.001
        const wind = Math.sin(time + leaf.x * 0.01) * 0.3
        
        // Update position with more natural movement
        leaf.y += leaf.fallSpeed
        leaf.x += Math.sin(leaf.y * 0.008 + leaf.phase) * 0.8 + wind
        leaf.rotation += leaf.rotationSpeed + wind * 0.1
        
        // Slight horizontal drift based on size (lighter leaves drift more)
        leaf.x += (leaf.size - 10) * 0.02
        
        // Draw leaf with calligraphy stroke shape
        ctx.save()
        ctx.translate(leaf.x, leaf.y)
        ctx.rotate(leaf.rotation)
        ctx.globalAlpha = leaf.opacity
        ctx.fillStyle = leaf.color
        
        // Draw tapered brush stroke shape
        ctx.beginPath()
        if (leaf.shape === 'oval') {
          // Standard oval
          ctx.ellipse(0, 0, leaf.size, leaf.size * 0.35, 0, 0, Math.PI * 2)
        } else if (leaf.shape === 'tapered') {
          // Tapered stroke like a brush
          ctx.moveTo(-leaf.size, 0)
          ctx.quadraticCurveTo(-leaf.size * 0.5, -leaf.size * 0.3, 0, -leaf.size * 0.2)
          ctx.quadraticCurveTo(leaf.size * 0.5, -leaf.size * 0.3, leaf.size, 0)
          ctx.quadraticCurveTo(leaf.size * 0.5, leaf.size * 0.3, 0, leaf.size * 0.2)
          ctx.quadraticCurveTo(-leaf.size * 0.5, leaf.size * 0.3, -leaf.size, 0)
        } else {
          // Diamond shape
          ctx.moveTo(0, -leaf.size)
          ctx.lineTo(leaf.size * 0.5, 0)
          ctx.lineTo(0, leaf.size)
          ctx.lineTo(-leaf.size * 0.5, 0)
          ctx.closePath()
        }
        ctx.fill()
        
        ctx.restore()
        
        // Keep leaf if still on screen with buffer
        return leaf.y < height + 100 && leaf.x > -100 && leaf.x < width + 100 && leaf.opacity > 0
      })
      
      // Spawn new leaves occasionally for ambient effect
      if (leavesRef.current.length < 20 && Math.random() < 0.02) {
        createLeaf(width, height, false)
      }
      
      animationRef.current = requestAnimationFrame(animate)
    }
    
    animationRef.current = requestAnimationFrame(animate)

    // Show enter prompt after animation
    const timer = setTimeout(() => {
      setShowEnterPrompt(true)
    }, 4000)

    return () => {
      window.removeEventListener('resize', resizeCanvas)
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
      clearTimeout(timer)
    }
  }, [reducedMotion])

  const createLeaf = (width: number, height: number, randomY: boolean = false, fromMouse: boolean = false) => {
    const shapes: Array<'oval' | 'tapered' | 'diamond'> = ['oval', 'tapered', 'tapered', 'oval', 'diamond']
    
    const leaf: Leaf = {
      x: fromMouse ? mousePosRef.current.x + (Math.random() - 0.5) * 80 : Math.random() * width,
      y: fromMouse ? mousePosRef.current.y + (Math.random() - 0.5) * 80 : (randomY ? Math.random() * height : -50),
      size: Math.random() * 12 + 4,
      rotation: Math.random() * Math.PI * 2,
      rotationSpeed: (Math.random() - 0.5) * 0.06,
      fallSpeed: Math.random() * 1.2 + 0.4,
      opacity: Math.random() * 0.4 + 0.3,
      color: LEAF_COLORS[Math.floor(Math.random() * LEAF_COLORS.length)],
      shape: shapes[Math.floor(Math.random() * shapes.length)],
      phase: Math.random() * Math.PI * 2,
    }
    leavesRef.current.push(leaf)
  }

  // Handle mouse/touch movement with throttling
  const handleMouseMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (reducedMotion) return
    
    const now = Date.now()
    if (now - lastMouseMoveRef.current < 80) return // Throttle to ~12fps for mouse
    lastMouseMoveRef.current = now

    let clientX, clientY
    if ('touches' in e) {
      clientX = e.touches[0].clientX
      clientY = e.touches[0].clientY
    } else {
      clientX = (e as React.MouseEvent).clientX
      clientY = (e as React.MouseEvent).clientY
    }

    mousePosRef.current = { x: clientX, y: clientY }

    // Spawn leaves near cursor
    if (leavesRef.current.length < MAX_LEAVES) {
      const spawnCount = 'touches' in e ? 3 : 2 // More leaves on touch
      for (let i = 0; i < spawnCount; i++) {
        createLeaf(window.innerWidth, window.innerHeight, false, true)
      }
    }
  }, [reducedMotion])

  const handleInteraction = () => {
    onEnter()
  }

  // Reduced motion version
  if (reducedMotion) {
    return (
      <div 
        className="relative h-screen w-full overflow-hidden bg-ink-black cursor-pointer"
        onClick={handleInteraction}
      >
        <div className="relative z-20 flex flex-col items-center justify-center h-full px-4">
          <div className="text-center">
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-warm-parchment tracking-wider uppercase">
              The Art of Tourism
            </h1>
          </div>

          <div className="mt-6 text-center">
            <span className="font-serif text-4xl md:text-6xl lg:text-7xl text-warm-parchment">
              旅遊之藝
            </span>
          </div>

          <div className="mt-8">
            <div className="w-16 h-16 md:w-20 md:h-20 border-4 border-seal-red bg-seal-red/10 flex items-center justify-center">
              <span className="font-serif text-seal-red text-2xl md:text-3xl font-bold">
                藝
              </span>
            </div>
          </div>

          <p className="mt-8 text-mist-gray text-lg md:text-xl tracking-widest uppercase">
            An Asia-Centered Philosophy
          </p>

          <div className="mt-12 text-center">
            <p className="text-muted-gold text-sm tracking-widest uppercase">
              Click to enter
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div 
      className="relative h-screen w-full overflow-hidden bg-ink-black cursor-pointer touch-none"
      onClick={handleInteraction}
      onMouseMove={handleMouseMove}
      onTouchMove={handleMouseMove}
      onTouchStart={handleMouseMove}
    >
      {/* Canvas for falling leaves */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 z-10 pointer-events-none"
        style={{ touchAction: 'none' }}
      />

      {/* Content */}
      <div className="relative z-20 flex flex-col items-center justify-center h-full px-4">
        <AnimatePresence>
          {/* English Title */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.5, ease: [0.4, 0, 0.2, 1] }}
            className="text-center"
          >
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-warm-parchment tracking-wider uppercase">
              The Art of Tourism
            </h1>
          </motion.div>

          {/* Chinese Title */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.8, ease: [0.4, 0, 0.2, 1] }}
            className="mt-6 text-center"
          >
            <span className="font-serif text-4xl md:text-6xl lg:text-7xl text-warm-parchment">
              旅遊之藝
            </span>
          </motion.div>

          {/* Seal Stamp */}
          <motion.div
            initial={{ opacity: 0, scale: 1.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 2, ease: 'easeOut' }}
            className="mt-8"
          >
            <div className="w-16 h-16 md:w-20 md:h-20 border-4 border-seal-red bg-seal-red/10 flex items-center justify-center animate-float">
              <span className="font-serif text-seal-red text-2xl md:text-3xl font-bold">
                藝
              </span>
            </div>
          </motion.div>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 2.5 }}
            className="mt-8 text-mist-gray text-lg md:text-xl tracking-widest uppercase"
          >
            An Asia-Centered Philosophy
          </motion.p>

          {/* Enter Prompt */}
          {showEnterPrompt && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mt-12 text-center"
            >
              <p className="text-muted-gold text-sm tracking-widest uppercase animate-pulse">
                Click or scroll to enter
              </p>
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="mt-4"
              >
                <svg 
                  className="w-6 h-6 mx-auto text-muted-gold" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M19 14l-7 7m0 0l-7-7m7 7V3" 
                  />
                </svg>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Gradient overlay */}
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-ink-black via-ink-black/95 to-ink-black pointer-events-none" />
    </div>
  )
}